// tslint:disable: max-file-line-count
import { ILocation } from '../../../../../api/locations';
import { EHouseMaterial, ESaleType, IJsonQuery } from '../../../../../json_query';
import { FDealType, FOfferType, isFlat, isUrban, isCommercial } from '../../../../../utils/category';
import { getRegionIds, MoscowId } from '../../../../../utils/geo';

import {
  UndergroundTime,
  Area,
  Bathroom,
  CeilingHeight,
  DemolishedInMoscowProgramm,
  Layout,
  Parking,
  HouseMaterial,
  HouseYear,
  Repair,
  SaleType,
  Floor,
  FloorsTotalContainer,
  RoomsInDealContainer,
  Windows,
  Elevators,
  Publish,
  WordsInclude,
  WordsExclude,
  Phone,
  OfferId,
  DwellingStatus,
  BathType,
  Facilities,
  RentTime,
  RentTerms,
  LivingTerms,
  Tenants,
  SuburbanUtilities,
  LandStatus,
  Heating,
  Bedrooms,
  SuburbanBathroom,
  RoomsTotal,
} from './components';

import { suburbanMaterials } from '../../../../state/tags/definitions/house_material';
import { isRoomTypeAvailable, isWithLayoutAvailable } from '../../../../../utils/layout';

import { IParam } from './types';
export { IParam };

export interface IParams {
  area: IParam; // Площадь
  bathrooms: IParam; // Санузел
  bathType: IParam; // Ванна/душ
  ceilingHeight: IParam; // Высота потолков
  demolishedInMoscowProgramm: IParam; // Дома под снос
  dwellingStatus: IParam; // Статус помещения
  elevators: IParam; // Лифтов минимум
  facilities: IParam; // Удобства
  floor: IParam; // Этаж
  floorsTotal: IParam; // Этажей в доме
  houseMaterial: IParam; // Тип дома
  houseYear: IParam; // Год постройки
  layout: IParam; // Планировка
  offerId: IParam; // Номер объявления
  parking: IParam; // Парковка
  phone: IParam; // Поиск по номеру телефона
  publish: IParam; // Дата публикации, Фото, Видео
  livingTerms: IParam; // Условия проживания
  rentTerms: IParam; // Условия сделки
  rentTime: IParam; // Срок аренды
  repair: IParam; // Ремонт
  saleType: IParam; // Тип продажи
  tenants: IParam; // Состав съемщиков
  undergroundTime: IParam; // До метро
  windows: IParam; // Вид из окна
  wordsExclude: IParam; // Исключить слова
  wordsInclude: IParam; // Содержит слова
  suburbanUtilities: IParam; // Наличие коммуникаций
  landStatus: IParam; // Статус участка
  heating: IParam; // Тип отопления
  bedrooms: IParam; // Спален в доме
  suburbanBathroom: IParam; // Санузел (загородная)
  roomsTotal: IParam; // Санузел (загородная)
  roomsInDeal: IParam; // Комнат в аренду/Комнат на продажу
}

export interface IGetParamsParameters {
  jsonQuery: IJsonQuery;
  currentLocation: ILocation | 'moscow_mo' | 'spb_lo';
  offerType: FOfferType;
  dealType: FDealType;
}

export const getParams = ({ jsonQuery, offerType, dealType, currentLocation }: IGetParamsParameters): IParams => {
  return {
    demolishedInMoscowProgramm: {
      title: 'Дома под снос',
      disabled: !(
        dealType & (FDealType.Sale | FDealType.RentLongterm) &&
        offerType & (FOfferType.Urban & ~FOfferType.FlatNew & ~FOfferType.Bed) &&
        (getRegionIds(currentLocation).some(id => id === MoscowId) ||
          (typeof currentLocation !== 'string' && currentLocation.parentId === MoscowId))
      ),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        demolished_in_moscow_programm: undefined,
      }),
      // tslint:disable-next-line: no-any
      component: DemolishedInMoscowProgramm as any,
    },

    wordsInclude: {
      title: 'Содержит слова в объявлении',
      component: WordsInclude,
    },

    wordsExclude: {
      title: 'Исключить слова в объявлении',
      component: WordsExclude,
    },

    phone: {
      title: 'Поиск по номеру телефона',
      // tslint:disable-next-line: no-any
      component: Phone as any,
    },

    offerId: {
      title: 'Поиск по номеру объявления',
      component: OfferId,
    },

    // Дата публикации, Фото, Видео
    publish: {
      title: 'Дата публикации',
      component: Publish,
    },

    layout: {
      title: 'Планировка',
      disabled: !(
        dealType & (FDealType.Sale | FDealType.RentLongterm) &&
        offerType & (FOfferType.Residential & ~FOfferType.Land) &&
        (isRoomTypeAvailable(jsonQuery) || isWithLayoutAvailable(jsonQuery))
      ),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        with_layout: undefined,
        room_type: undefined,
      }),
      distinctJsonQuery: jsonQuery => {
        if (!isRoomTypeAvailable(jsonQuery)) {
          // tslint:disable-next-line: no-parameter-reassignment
          jsonQuery = { ...jsonQuery, room_type: undefined };
        }

        if (!isWithLayoutAvailable(jsonQuery)) {
          // tslint:disable-next-line: no-parameter-reassignment
          jsonQuery = { ...jsonQuery, with_layout: undefined };
        }

        return jsonQuery;
      },
      component: Layout,
    },

    area: {
      title: isUrban(offerType) ? 'Площадь, м\u00B2' : 'Площадь',
      distinctJsonQuery: jsonQuery => {
        if ((offerType & (FOfferType.Room | FOfferType.Bed)) === 0) {
          return {
            ...jsonQuery,
            room_area: undefined,
          };
        }

        if ((offerType & (FOfferType.Flat | FOfferType.FlatShared)) === 0) {
          return {
            ...jsonQuery,
            living_area: undefined,
          };
        }

        return jsonQuery;
      },
      clearJsonQuery: jsonQuery => {
        if (offerType & (FOfferType.Land | FOfferType.CommercialLand)) {
          // tslint:disable-next-line: no-parameter-reassignment
          jsonQuery = {
            ...jsonQuery,
            total_area: undefined,
          };
        }

        return {
          ...jsonQuery,
          room_area: undefined,
          living_area: undefined,
          kitchen: undefined,
        };
      },
      component: Area,
    },

    floor: {
      title: 'Этаж',
      disabled: !(
        offerType &
        (FOfferType.Urban |
          FOfferType.Office |
          FOfferType.TradeArea |
          FOfferType.Manufacture |
          FOfferType.Warehouse |
          FOfferType.FreeAppointmentObject |
          FOfferType.PublicCatering |
          FOfferType.AutoService |
          FOfferType.DomesticServices |
          FOfferType.Business)
      ),
      distinctJsonQuery: jsonQuery => {
        if (!isUrban(offerType)) {
          return {
            ...jsonQuery,
            is_first_floor: undefined,
            not_last_floor: undefined,
          };
        } else if (
          (offerType &
            (FOfferType.Office |
              FOfferType.TradeArea |
              FOfferType.Manufacture |
              FOfferType.Warehouse |
              FOfferType.FreeAppointmentObject |
              FOfferType.PublicCatering |
              FOfferType.AutoService |
              FOfferType.DomesticServices)) ===
          0
        ) {
          return {
            ...jsonQuery,
            is_basement: undefined,
            is_semibasement: undefined,
            is_first_floor: isUrban(offerType) || isFlat(offerType) ? jsonQuery.is_first_floor : undefined,
          };
        }

        return jsonQuery;
      },
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        floor: undefined,
        is_basement: undefined,
        is_semibasement: undefined,
        is_first_floor: undefined,
        not_last_floor: undefined,
      }),
      component: Floor,
    },

    // Этажей в доме/здании
    floorsTotal: {
      title: `Этажей в ${!isCommercial(offerType) ? 'доме' : 'здании'}`,
      disabled: !(
        offerType &&
        (FOfferType.Residential & ~FOfferType.Land) |
          FOfferType.Office |
          FOfferType.TradeArea |
          FOfferType.Manufacture |
          FOfferType.Warehouse |
          FOfferType.FreeAppointmentObject |
          FOfferType.Building |
          FOfferType.Business
      ),
      clearJsonQuery: nextJsonQuery => {
        return {
          ...nextJsonQuery,
          floorn: undefined,
        };
      },
      component: FloorsTotalContainer,
    },

    houseMaterial: {
      title: 'Тип дома',
      disabled: !(offerType & (FOfferType.Residential & ~FOfferType.Land)),
      distinctJsonQuery: jsonQuery => {
        let nextHouseMaterials =
          jsonQuery.house_material &&
          jsonQuery.house_material.value.filter((type: EHouseMaterial) => {
            if (offerType === FOfferType.FlatNew) {
              return ([
                EHouseMaterial.Panel,
                EHouseMaterial.Brick,
                EHouseMaterial.Monolith,
              ] as EHouseMaterial[]).includes(type);
            } else if (offerType & FOfferType.Suburban) {
              return suburbanMaterials.includes(type);
            }

            return true;
          });

        if (nextHouseMaterials && nextHouseMaterials.length === 0) {
          nextHouseMaterials = undefined;
        }

        return {
          ...jsonQuery,
          house_material: nextHouseMaterials
            ? {
                type: 'terms',
                value: nextHouseMaterials,
              }
            : undefined,
        };
      },
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        house_material: undefined,
      }),
      component: HouseMaterial,
    },

    houseYear: {
      title: 'Год постройки',
      disabled: !(offerType & (FOfferType.Residential & ~FOfferType.FlatNew & ~FOfferType.Land)),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        house_year: undefined,
      }),
      // tslint:disable-next-line: no-any
      component: HouseYear as any,
    },

    ceilingHeight: {
      title: 'Высота потолков',
      disabled: !(
        offerType &
        ((FOfferType.Urban & ~FOfferType.FlatNew) | FOfferType.Manufacture | FOfferType.Warehouse)
      ),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        ceiling_height: undefined,
      }),
      component: CeilingHeight,
    },

    elevators: {
      title: 'Лифт',
      disabled: !(offerType & (FOfferType.Urban & ~FOfferType.FlatNew)),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        lifts: undefined,
        lift_service: undefined,
      }),
      component: Elevators,
    },

    windows: {
      title: 'Вид из окна',
      disabled: !(offerType & FOfferType.Urban),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        windows_type: undefined,
        balconies: undefined,
        loggia: undefined,
      }),
      // tslint:disable-next-line: no-any
      component: Windows as any,
    },

    repair: {
      title: 'Ремонт',
      disabled:
        (offerType & (FOfferType.Urban & ~FOfferType.FlatNew)) === 0 &&
        (offerType & (FOfferType.Suburban & ~FOfferType.Land)) === 0,
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        repair: undefined,
      }),
      // tslint:disable-next-line: no-any
      component: Repair as any,
    },

    bathrooms: {
      title: 'Санузел',
      disabled: (offerType & (FOfferType.Urban & ~FOfferType.FlatNew)) === 0,
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        isWCfixed: undefined,
        wc_type: undefined,
        wc: undefined,
      }),
      // tslint:disable-next-line: no-any
      component: Bathroom as any,
    },

    suburbanBathroom: {
      title: 'Санузел',
      disabled: (offerType & (FOfferType.Suburban & ~FOfferType.Land)) === 0,
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        wc_site: undefined,
      }),
      component: SuburbanBathroom,
    },

    dwellingStatus: {
      title: 'Апартаменты',
      disabled: !(offerType & (FOfferType.Flat | FOfferType.FlatShared)),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        apartment: undefined,
        only_flat: undefined,
        penthouse: undefined,
      }),
      // tslint:disable-next-line: no-any
      component: DwellingStatus as any,
    },

    saleType: {
      title: 'Тип продажи',
      disabled: !(dealType & FDealType.Sale && offerType & FOfferType.Urban),
      distinctJsonQuery: jsonQuery => {
        let value =
          jsonQuery.sost_type &&
          jsonQuery.sost_type.value.filter(type => {
            const includes = [ESaleType.F3, ESaleType.GSKContract, ESaleType.Reassignment].includes(type);

            if (offerType === FOfferType.FlatNew) {
              return includes;
            } else {
              return !includes;
            }
          });

        if (value && value.length === 0) {
          value = undefined;
        }

        return {
          ...jsonQuery,
          sost_type: value
            ? {
                type: 'terms',
                value,
              }
            : undefined,
        };
      },
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        sost_type: undefined,
        ipoteka: undefined,
      }),
      component: SaleType,
    },

    parking: {
      title: 'Парковка',
      disabled: !(
        offerType &
        ((FOfferType.Urban & ~FOfferType.FlatNew) |
          FOfferType.Office |
          FOfferType.Building |
          FOfferType.Garage |
          FOfferType.Business)
      ),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        parking_type: undefined,
      }),
      // tslint:disable-next-line: no-any
      component: Parking as any,
    },

    undergroundTime: {
      title: 'До метро',
      disabled: !(typeof currentLocation === 'string' || currentLocation.hasMetro),
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        only_foot: undefined,
        foot_min: undefined,
      }),
      // tslint:disable-next-line: no-any
      component: UndergroundTime as any,
    },

    bathType: {
      title: 'Ванна/душ',
      // tslint:disable-next-line: no-any
      component: BathType as any,
    },

    facilities: {
      title: 'Удобства',
      disabled: (dealType & FDealType.Rent) === 0 || (offerType & (FOfferType.Residential & ~FOfferType.Land)) === 0,
      component: Facilities,
    },

    tenants: {
      title: 'Состав съемщиков',
      disabled: !(offerType & FOfferType.Room || offerType & FOfferType.Bed),
      component: Tenants,
    },

    rentTime: {
      disabled: (dealType & FDealType.RentLongterm) === 0,
      title: 'Срок аренды',
      component: RentTime,
    },

    rentTerms: {
      disabled: (dealType & FDealType.RentLongterm) === 0,
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, commission_type: undefined, prepay: undefined, zalog: undefined };
      },
      title: 'Условия сделки',
      component: RentTerms,
    },

    livingTerms: {
      disabled: (dealType & FDealType.RentLongterm) === 0,
      title: 'Условия проживания',
      component: LivingTerms,
    },

    suburbanUtilities: {
      title: 'Наличие коммуникаций',
      component: SuburbanUtilities,
    },

    landStatus: {
      title: 'Статус участка',
      component: LandStatus,
    },

    heating: {
      title: 'Тип отопления',
      component: Heating,
    },

    bedrooms: {
      title: 'Спален в доме',
      component: Bedrooms,
    },

    roomsTotal: {
      disabled: !(offerType & (FOfferType.Room | FOfferType.FlatShared | FOfferType.Bed)),
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, rooms_count: undefined };
      },
      title: 'Комнат в квартире',
      component: RoomsTotal,
    },

    roomsInDeal: {
      disabled: !(offerType & (FOfferType.Room | FOfferType.Bed)),
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, rooms_for_sale: undefined };
      },
      title: `Комнат ${(FDealType.Rent & dealType && 'в аренду') || 'на продажу'}`,
      component: RoomsInDealContainer,
    },
  };
};
